import React, { FC, useCallback, useState, useRef } from 'react';
import { CategoryProps, Chat } from './chat';
import { Message } from '../../components/chat/types';
import {
  getIAResposta,
  getConversationId,
  getAIAudioReponse,
} from '../../apis/ia';
import { cancelarVoz } from '../../util/voice';
import { useSharedChat } from '../../components/chat/hooks/useSharedChat';
import { MESSAGE_TYPES } from './constants';
import {
  createLiaMessage,
  createErrorMessage,
  createLiaAudioMessage,
} from './utils';

import { useWebgate } from '@xvia/webgate-connect';

export const ChatContainer: FC = () => {
  const conversationIdRef = useRef<string | null | undefined>(null);
  const validConversationIdRef = useRef<boolean>(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);

  const { messages, setMessages, input, setInput } = useSharedChat();
  const [loading, setLoading] = useState<boolean>(false);
  const userInfoInjected = useRef(false);

  const { userInfo, accessToken } = useWebgate();

  const updateConversationId = useCallback((id: string | null | undefined) => {
    conversationIdRef.current = id;
    setConversationId(id || undefined);
  }, []);

  const resolveConversationId = useCallback(async () => {
    if (conversationIdRef.current) return conversationIdRef.current;
    const id = await getConversationId(accessToken, userInfo);
    updateConversationId(id);
    return id;
  }, [accessToken, userInfo, updateConversationId]);

  const processUserMessage = useCallback(
    async (message: Message) => {
      cancelarVoz();

      if (message.from === MESSAGE_TYPES.USER) {
        setLoading(true);
      }

      let messageText = typeof message.text === 'string' ? message.text : '';

      try {
        setMessages((prev) => [...prev, message]);

        if (userInfo) {
          messageText = `Pergunta do usário: ` + messageText;
          userInfoInjected.current = true;
        } else if (!userInfo && userInfoInjected.current) {
          userInfoInjected.current = false;
          updateConversationId(null);

          localStorage.removeItem('conversationId');
        }

        if (!conversationIdRef.current && !validConversationIdRef.current) {
          const newConversationId = await getConversationId(
            accessToken,
            userInfo
          );
          updateConversationId(newConversationId);
          validConversationIdRef.current = true;
        }

        const response = await getIAResposta(
          messageText,
          userInfo,
          accessToken,
          conversationIdRef.current
        );

        if (message.type === MESSAGE_TYPES.AUDIO) {
          const audioResponse = await getAIAudioReponse(
            response,
            userInfo,
            accessToken,
            conversationIdRef.current
          );
          const liaAudioMessage = createLiaAudioMessage(audioResponse);
          return setMessages((prev) => [...prev, liaAudioMessage]);
        }

        const liaMessage = createLiaMessage(response.toString());
        setMessages((prev) => [...prev, liaMessage]);
      } catch (error) {
        console.error('Erro ao obter resposta da IA:', error);
        const errorMessage = createErrorMessage();
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setLoading(false);
      }
    },
    [setMessages, userInfo, updateConversationId, accessToken]
  );

  const props: CategoryProps = {
    messages,
    input,
    setInput,
    setMessages: processUserMessage,
    loading,
    setLoading,
    conversationId,
    onResolveConversationId: resolveConversationId,
  };

  return <Chat {...props} />;
};
